import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import moment from 'moment';
import { MapPin } from 'lucide-react';
import { useScrollVisibility } from '../../../hooks/useScrollVisibility';
import { useCalendarDragDrop, RescheduleRequest } from '../hooks/useCalendarDragDrop';
import { useTaskToTimeBoxDrag } from '../../../hooks/useTaskToTimeBoxDrag';
import { ScheduleItem } from '../../../types';
import { TimeBox } from '../../../types/timeBox';
import { Event } from '../../../types/calendar-event';
import { Task } from '../../../types/task';
import { IdealWeek } from '../../../types/user';
import AllDayEventsSection from './AllDayEventsSection';
import {
  generateHours,
  getScheduleItemsForDay,
  calculateScheduleItemPosition,
  processOverlappingScheduleItems,
  separateAllDayAndTimedItems,
  getItemDisplayTitle,
  formatTimeRange,
  getEventStyleClasses,
  getTimeBoxStyleClasses,
  CARD_SPACING,
  SCROLL_CONFIG,
  DISPLAY_CONFIG,
  DRAG_CONFIG,
  scrollToCurrentTime
} from '../../../utils/calendarHelper';
import DateRangeButton from '../../../components/common/DateRangeButton';

interface DayViewProps {
  date: Date;
  scheduleItems: ScheduleItem[];
  onSelectTimeBox: (item: ScheduleItem) => void;
  onSelectSlot: (start: Date, end: Date) => void;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  onNavigateToday: () => void;
  onDateSelect: (date: Date) => void;
  onReschedule?: (request: RescheduleRequest) => Promise<void>;
  /** 從 Task 創建 TimeBox 的回調（用於 Planned Layer 拖曳） */
  onCreateTimeBoxFromTask?: (task: Task, startTime: Date, endTime: Date) => Promise<void>;
  /** 將 Task 添加到既有 TimeBox 的回調 */
  onAddTaskToTimeBox?: (timeBoxId: string, taskId: string) => Promise<void>;
  /** 獲取 Task 詳情的函數 */
  getTaskById?: (taskId: string) => Task | undefined;
  /** 觸控拖曳就緒回調（DayView 通過此回調傳遞觸控處理器） */
  onTouchDragReady?: (handler: (task: Task, touch: Touch) => void) => void;
  /** Ideal Week 主題日設定 */
  idealWeek?: IdealWeek;
}

// 移除重複的輔助函數，統一使用 calendarHelper 中的函數

const DayView: React.FC<DayViewProps> = ({
  date,
  scheduleItems,
  onSelectTimeBox,
  onSelectSlot,
  onNavigatePrev,
  onNavigateNext,
  onNavigateToday,
  onDateSelect,
  onReschedule,
  onCreateTimeBoxFromTask,
  onAddTaskToTimeBox,
  getTaskById,
  onTouchDragReady,
  idealWeek
}) => {
  // 判斷當前顯示的日期是否為今天
  const isToday = useMemo(() => {
    const now = new Date();
    return date.getDate() === now.getDate() &&
           date.getMonth() === now.getMonth() &&
           date.getFullYear() === now.getFullYear();
  }, [date]);

  // 滾動顯示/隱藏工具欄
  const isToolbarVisible = useScrollVisibility(50);

  // 時間格區域的 ref（用於拖拽）
  const timeGridContainerRef = useRef<HTMLDivElement>(null);

  // 使用拖拽 Hook（TimeBox 重新排程）
  const {
    dragState,
    isDragging,
    handleMouseDown,
    handleTouchStart,
    getDragPreviewStyle,
    getCursorStyle,
  } = useCalendarDragDrop({
    hourHeight: DISPLAY_CONFIG.HOUR_HEIGHT,
    startHour: DISPLAY_CONFIG.START_HOUR,
    endHour: DISPLAY_CONFIG.END_HOUR,
    onReschedule,
    enabled: !!onReschedule,
  });

  // 使用 Task 拖曳 Hook（從 Planned Layer 拖曳任務創建 TimeBox）
  const {
    dropPreview,
    isDragOver,
    dropTargetTimeBox,
    handleDragOver: handleTaskDragOver,
    handleDragLeave: handleTaskDragLeave,
    handleDrop: handleTaskDrop,
    handleDragOverTimeBox,
    handleDragLeaveTimeBox,
    handleDropOnTimeBox,
    getDropPreviewStyle,
    startTouchDrag,
  } = useTaskToTimeBoxDrag({
    hourHeight: DISPLAY_CONFIG.HOUR_HEIGHT,
    startHour: DISPLAY_CONFIG.START_HOUR,
    endHour: DISPLAY_CONFIG.END_HOUR,
    timeColumnWidth: DISPLAY_CONFIG.TIME_COLUMN_WIDTH,
    days: [date], // 單日視圖只有一天
    onCreateTimeBox: onCreateTimeBoxFromTask,
    onAddTaskToTimeBox,
    getTaskById,
    enabled: !!onCreateTimeBoxFromTask,
  });

  // 處理卡片的鼠標按下事件
  const handleCardMouseDown = useCallback(
    (e: React.MouseEvent, item: ScheduleItem) => {
      if (!timeGridContainerRef.current || !onReschedule) return;
      const containerRect = timeGridContainerRef.current.getBoundingClientRect();
      handleMouseDown(e, item, containerRect);
    },
    [handleMouseDown, onReschedule]
  );

  // 處理卡片的觸控開始事件（平板拖曳 TimeBox）
  const handleCardTouchStart = useCallback(
    (e: React.TouchEvent, item: ScheduleItem) => {
      if (!timeGridContainerRef.current || !onReschedule) return;
      const containerRect = timeGridContainerRef.current.getBoundingClientRect();
      handleTouchStart(e, item, containerRect);
    },
    [handleTouchStart, onReschedule]
  );

  // 處理卡片點擊（區分拖拽和點擊）
  const handleCardClick = useCallback(
    (e: React.MouseEvent, item: ScheduleItem) => {
      // 如果正在拖拽，不觸發點擊
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onSelectTimeBox(item);
    },
    [isDragging, onSelectTimeBox]
  );

  // 處理從 PlannedLayer 觸控拖曳任務
  const handleTaskTouchStartInternal = useCallback(
    (task: Task, touch: Touch) => {
      if (!timeGridContainerRef.current) return;
      const containerRect = timeGridContainerRef.current.getBoundingClientRect();
      startTouchDrag(task, touch, containerRect);
    },
    [startTouchDrag]
  );

  // 將觸控處理器暴露給父組件
  useEffect(() => {
    if (onTouchDragReady) {
      onTouchDragReady(handleTaskTouchStartInternal);
    }
  }, [onTouchDragReady, handleTaskTouchStartInternal]);

  // 處理鼠標移入卡片時的游標樣式
  const [hoverCursor, setHoverCursor] = useState<string>('pointer');
  const handleCardMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!onReschedule) return;
      const element = e.currentTarget as HTMLElement;
      const rect = element.getBoundingClientRect();
      const mouseY = e.clientY - rect.top;
      const cursor = getCursorStyle(mouseY, rect.height);
      setHoverCursor(cursor);
    },
    [getCursorStyle, onReschedule]
  );

  // 使用共用配置
  const startHour = DISPLAY_CONFIG.START_HOUR;
  const endHour = DISPLAY_CONFIG.END_HOUR;

  // 獲取當天的所有日程項目
  const dayScheduleItems = getScheduleItemsForDay(scheduleItems, date);

  // 使用統一的重疊處理函數
  const { allDayEvents, timedItems } = useMemo(() => {
    // 使用共用工具函數分離全天事件和定時項目
    const { allDayEvents, timedItems: timedScheduleItems } = separateAllDayAndTimedItems(dayScheduleItems);

    // 只處理定時項目的重疊，全天事件不參與時間槽計算
    const processedTimedItems = processOverlappingScheduleItems(timedScheduleItems, startHour, endHour);

    return {
      allDayEvents: allDayEvents,
      timedItems: processedTimedItems
    };
  }, [dayScheduleItems]); // startHour 和 endHour 是來自 DISPLAY_CONFIG 的常數
  const hours = generateHours(startHour, endHour);
  const hourHeight = DISPLAY_CONFIG.HOUR_HEIGHT;
  const MIN_TIMEBOX_HEIGHT = DISPLAY_CONFIG.MIN_TIMEBOX_HEIGHT;

  // ref 用於時間區域的滾動
  const timeGridRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // 時間列寬度常數
  const timeColumnWidth = DISPLAY_CONFIG.TIME_COLUMN_WIDTH;


  // 自動滾動到當前時間（只在顯示今天時才滾動）
  useEffect(() => {
    const timer = setTimeout(() => {
      // 檢查是否顯示的是今天
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();

      // 只在顯示今天時才滾動
      if (isToday && timeGridRef.current) {
        scrollToCurrentTime(timeGridRef.current, startHour, hourHeight);
      }
    }, SCROLL_CONFIG.INITIAL_DELAY);

    return () => clearTimeout(timer);
  }, [date.getTime()]);




  // allDayEvents 和 timedItems 已在上面的 useMemo 中分離和處理

  // 渲染當前時間指示線
  const renderCurrentTimeIndicator = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // 只在顯示今天時才顯示指示線
    if (date.toDateString() !== now.toDateString()) {
      return null;
    }

    // 計算指示線的垂直位置
    const top = hourHeight * (currentHour - startHour) +
      (currentMinute / 60) * hourHeight;

    return (
      <div
        className="absolute flex items-center z-floating"
        style={{
          top: `${top}px`,
          left: `${timeColumnWidth}px`,
          right: 0
        }}
      >
        <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></div>
        <div className="flex-1 h-0.5 bg-red-500"></div>
      </div>
    );
  };


  return (
    <div className="flex flex-col h-full w-full relative">
      {/* 工具欄 */}
      <div className={`
        flex items-center justify-between py-3 px-4
        border-b border-[var(--border-default)] bg-surface-page
        sticky top-0 z-toolbar
        transition-transform duration-normal ease-in-out
        ${isToolbarVisible ? 'translate-y-0' : '-translate-y-full'}
      `}>
        <DateRangeButton
          dateText={moment(date).format('YYYY/MM/DD ddd')}
          currentDate={date}
          onDateSelect={onDateSelect}
          isMobile={false}
        />

        <div className="flex items-center space-x-2">
          <button
            onClick={onNavigatePrev}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Previous"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          <button
            onClick={onNavigateToday}
            className="px-3 py-1.5 bg-sky-100 text-sky-700 rounded-md hover:bg-sky-200 transition-colors text-sm font-medium"
          >
            Today
          </button>

          <button
            onClick={onNavigateNext}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Next"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* 固定表頭 */}
      <div ref={headerRef} className={`
        flex-shrink-0 flex border-b border-[var(--border-default)] w-full bg-surface-page
        sticky z-sticky
        transition-[top] duration-normal ease-in-out
        ${isToolbarVisible ? 'top-16' : 'top-0'}
      `}>
        {/* 時間列標頭 */}
        <div
          style={{ width: `${timeColumnWidth}px`, minWidth: `${timeColumnWidth}px` }}
          className="text-center py-2 font-medium text-gray-500"
        >
          Time
        </div>
        {/* 日期列標頭 */}
        <div className="flex-1 text-center py-2">
          <div className="flex items-center justify-center gap-1 font-medium text-gray-500">
            <span>{moment(date).format('ddd')}</span>
            {isToday ? (
              <span aria-current="date" className="inline-flex items-center justify-center w-7 h-7 bg-sky-500 text-white font-semibold rounded-full text-sm">
                {date.getDate()}
              </span>
            ) : (
              <span>{date.getDate()}</span>
            )}
          </div>
          {idealWeek?.[date.getDay() as keyof IdealWeek] && (
            <div
              className="text-xs text-sky-500 font-normal"
              title={idealWeek[date.getDay() as keyof IdealWeek]?.description || undefined}
            >
              {idealWeek[date.getDay() as keyof IdealWeek]?.title}
            </div>
          )}
        </div>
      </div>

      {/* 全天事件區域 */}
      {allDayEvents.length > 0 && (
        <AllDayEventsSection
          columns={[{ events: allDayEvents as Event[] }]}
          onSelectEvent={(event) => onSelectTimeBox(event)}
          timeColumnWidth={DISPLAY_CONFIG.TIME_COLUMN_WIDTH}
        />
      )}

      {/* 時間格和時間箱 - 可滾動部分 */}
      <div className="flex-1 overflow-y-auto hide-scrollbar" ref={timeGridRef}>
        <div
          className={`relative ${isDragOver ? 'bg-sky-50/30' : ''}`}
          ref={timeGridContainerRef}
          onDragOver={(e) => {
            if (!timeGridContainerRef.current) return;
            const containerRect = timeGridContainerRef.current.getBoundingClientRect();
            handleTaskDragOver(e, containerRect, 0);
          }}
          onDragLeave={handleTaskDragLeave}
          onDrop={(e) => {
            if (!timeGridContainerRef.current) return;
            const containerRect = timeGridContainerRef.current.getBoundingClientRect();
            handleTaskDrop(e, containerRect, 0);
          }}
        >
          {/* 時間格線 - 15 分鐘細分 */}
          {hours.map(hour => (
            <div
              key={hour}
              className="flex border-b border-gray-100"
              style={{ height: `${hourHeight}px` }}
            >
              <div
                style={{ width: `${timeColumnWidth}px`, minWidth: `${timeColumnWidth}px` }}
                className="text-center text-sm text-gray-500 border-r border-gray-200 py-1"
              >
                {hour}:00
              </div>
              <div className="flex-1 flex flex-col">
                {/* 4 個 15 分鐘的子格子 */}
                {[0, 15, 30, 45].map((minute, idx) => (
                  <div
                    key={minute}
                    className={`flex-1 relative cursor-pointer
                      hover:border-2 hover:border-dashed hover:border-sky-300
                      hover:bg-sky-500/5 hover:rounded-md hover:z-10
                      transition-all duration-200 ${idx < 3 ? 'border-b border-gray-50' : ''}`}
                    onClick={(e) => {
                      const start = new Date(date);
                      start.setHours(hour, minute, 0, 0);
                      const end = new Date(start);
                      end.setTime(start.getTime() + 60 * 60 * 1000);
                      onSelectSlot(start, end);
                    }}
                  ></div>
                ))}
              </div>
            </div>
          ))}

          {/* 當前時間指示線 */}
          {renderCurrentTimeIndicator()}


          {/* 統一渲染所有定時項目（TimeBox 和 Event） */}
          {timedItems.map((item) => {
            const hour = item.startTime.getHours();
            if (hour < startHour || hour > endHour) return null;

            const { top, height: originalHeight } = calculateScheduleItemPosition(
              item as ScheduleItem,
              hourHeight,
              startHour
            );

            // 應用最小高度
            const height = Math.max(originalHeight, MIN_TIMEBOX_HEIGHT);

            // 使用統一的間距常數
            const marginPx = CARD_SPACING.margin;
            const verticalGap = CARD_SPACING.vertical;

            // 根據重疊情況計算寬度和偏移（統一的錯開邏輯）
            const containerWidth = `calc(100% - ${timeColumnWidth}px)`;
            const positionWidth = item.totalPositions > 1
              ? `calc((${containerWidth} - ${marginPx * 2}px) / ${item.totalPositions})`
              : `calc(${containerWidth} - ${marginPx * 2}px)`;

            const positionOffset = item.totalPositions > 1
              ? `calc((${containerWidth} - ${marginPx * 2}px) * ${item.position} / ${item.totalPositions})`
              : '0px';

            // 根據項目類型選擇樣式
            const styleClasses = item.itemType === 'timebox'
              ? getTimeBoxStyleClasses(item as TimeBox)
              : getEventStyleClasses(item as Event);

            // 檢查是否正在拖拽此項目
            const isBeingDragged = dragState?.itemId === item.id;
            const dragPreviewStyle = getDragPreviewStyle(item as ScheduleItem);

            // 計算實際顯示的位置（如果正在拖拽則使用拖拽狀態的位置）
            const displayTop = isBeingDragged && dragPreviewStyle
              ? dragPreviewStyle.top
              : `${top + verticalGap}px`;
            const displayHeight = isBeingDragged && dragPreviewStyle
              ? dragPreviewStyle.height
              : `${height - verticalGap}px`;

            // 檢查是否是拖曳目標（高亮反饋）
            const isDropTarget = dropTargetTimeBox === item.id;

            return (
              <div
                key={`${item.itemType}-${item.id}`}
                className={`absolute px-2 py-1 overflow-hidden ${styleClasses} ${onReschedule ? 'select-none' : 'cursor-pointer'
                  } ${isBeingDragged ? 'ring-2 ring-sky-400 ring-opacity-50 z-drag-active' : ''
                  } ${isDropTarget ? 'ring-2 ring-sky-500 ring-opacity-60 bg-sky-50' : ''}`}
                style={{
                  top: displayTop,
                  left: `calc(${timeColumnWidth}px + ${positionOffset})`,
                  height: displayHeight,
                  width: positionWidth,
                  marginLeft: `${marginPx}px`,
                  marginRight: `${marginPx}px`,
                  borderRadius: `${CARD_SPACING.borderRadius}px`,
                  cursor: onReschedule ? hoverCursor : 'pointer',
                  opacity: isBeingDragged ? 0.9 : 1,
                  boxShadow: isBeingDragged ? '0 4px 12px rgba(0, 0, 0, 0.15)' : undefined,
                  transition: isBeingDragged ? 'none' : 'box-shadow 0.2s',
                }}
                onMouseDown={(e) => handleCardMouseDown(e, item as ScheduleItem)}
                onTouchStart={(e) => handleCardTouchStart(e, item as ScheduleItem)}
                onMouseMove={handleCardMouseMove}
                onClick={(e) => handleCardClick(e, item as ScheduleItem)}
                // TimeBox 拖曳事件（支持將 Task 拖入既有 TimeBox）
                {...(item.itemType === 'timebox' && onAddTaskToTimeBox ? {
                  onDragOver: (e) => handleDragOverTimeBox(item.id, e),
                  onDragLeave: handleDragLeaveTimeBox,
                  onDrop: (e) => handleDropOnTimeBox(item.id, e),
                } : {})}
              >
                {/* 頂部縮放手柄區域指示器 */}
                {onReschedule && (
                  <div
                    className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 hover:opacity-100 bg-gradient-to-b from-black/10 to-transparent rounded-t-md transition-opacity"
                    style={{ height: `${DRAG_CONFIG.RESIZE_HANDLE_HEIGHT}px` }}
                  />
                )}

                <div className="text-xs font-medium truncate">{getItemDisplayTitle(item as ScheduleItem)}</div>
                <div className="text-xs text-gray-600">
                  {/* 顯示拖拽時的新時間預覽 */}
                  {isBeingDragged && dragState ? (
                    `${moment(dragState.currentStartTime).format('HH:mm')} - ${moment(dragState.currentEndTime).format('HH:mm')}`
                  ) : (
                    formatTimeRange(item as ScheduleItem)
                  )}
                </div>
                {item.location && (
                  <div className="text-xs text-gray-600 truncate flex items-center gap-1">
                    <MapPin size={12} className="flex-shrink-0" />
                    <span className="truncate">{item.location}</span>
                  </div>
                )}
                {/* TimeBox 特有的任務標識 */}
                {item.itemType === 'timebox' && item.taskId && (
                  <div className="mt-1">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-sky-100 text-sky-800">
                      Task
                    </span>
                  </div>
                )}

                {/* 底部縮放手柄區域指示器 */}
                {onReschedule && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 hover:opacity-100 bg-gradient-to-t from-black/10 to-transparent rounded-b-md transition-opacity"
                    style={{ height: `${DRAG_CONFIG.RESIZE_HANDLE_HEIGHT}px` }}
                  />
                )}
              </div>
            );
          })}

          {/* Task 拖曳放置預覽（懸停在 Timebox 上時隱藏） */}
          {dropPreview && dropPreview.isActive && !dropTargetTimeBox && (
            <div
              className="border-2 border-dashed border-sky-400 bg-sky-50/50 rounded-md flex items-center justify-center"
              style={getDropPreviewStyle() || undefined}
            >
              <div className="text-xs text-sky-600 font-medium">
                {moment(dropPreview.startTime).format('HH:mm')} - {moment(dropPreview.endTime).format('HH:mm')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DayView;